import React, { useEffect, useState } from 'react';
import {
  Box, Card, Stack, Button, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Typography,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, MenuItem, FormControlLabel, Switch, Alert,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { api } from '../../config/api';

const clp = (n: any) => `$${Math.round(Number(n || 0)).toLocaleString('es-CL')}`;
const VACIO = { codigo: '', descripcion: '', tipo: 'porcentaje', valor: 20, plan_codigo: '', usos_max: '', un_uso_por_usuario: true, vence_at: '', activo: true };

const AdminCupones: React.FC = () => {
  const [cupones, setCupones] = useState<any[]>([]);
  const [planes, setPlanes] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>(null);
  const [error, setError] = useState('');

  const cargar = () => api.get('/admin/cupones').then((r) => setCupones(r.data.cupones || []));
  useEffect(() => { cargar(); api.get('/admin/planes').then((r) => setPlanes(r.data.planes || [])); }, []);

  const abrir = (c?: any) => {
    setError('');
    setEdit(c ? { ...c, plan_codigo: c.plan_codigo || '', usos_max: c.usos_max ?? '', vence_at: c.vence_at ? String(c.vence_at).slice(0, 10) : '' } : { ...VACIO });
  };
  const guardar = async () => {
    setError('');
    try {
      if (edit.id) await api.put(`/admin/cupones/${edit.id}`, edit); else await api.post('/admin/cupones', edit);
      setEdit(null); cargar();
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo guardar'); }
  };
  const set = (k: string) => (e: any) => setEdit({ ...edit, [k]: e.target.value });
  const vencido = (c: any) => c.vence_at && new Date(c.vence_at).getTime() < Date.now();

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => abrir()}>Nuevo cupón</Button>
      </Stack>
      <Card><TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Código</TableCell><TableCell>Descuento</TableCell><TableCell>Plan</TableCell><TableCell>Usos</TableCell>
            <TableCell>Vence</TableCell><TableCell>Descuento total</TableCell><TableCell>Estado</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>
            {cupones.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{c.codigo}</Typography><Typography variant="caption" color="text.secondary">{c.descripcion}</Typography></TableCell>
                <TableCell>{c.tipo === 'monto' ? clp(c.valor) : `${c.valor}%`}</TableCell>
                <TableCell>{c.plan_codigo || 'Todos'}</TableCell>
                <TableCell>{c.usos}{c.usos_max != null ? ` / ${c.usos_max}` : ''}{c.un_uso_por_usuario ? ' · 1 por usuario' : ''}</TableCell>
                <TableCell>{c.vence_at ? new Date(c.vence_at).toLocaleDateString('es-CL') : '—'}</TableCell>
                <TableCell>{clp(c.descuento_total)}</TableCell>
                <TableCell><Chip size="small" color={c.activo && !vencido(c) ? 'success' : 'default'} label={!c.activo ? 'Inactivo' : vencido(c) ? 'Vencido' : 'Activo'} /></TableCell>
                <TableCell><IconButton size="small" onClick={() => abrir(c)}><Edit fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {cupones.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin cupones</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer></Card>

      <Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edit?.id ? `Editar ${edit.codigo}` : 'Nuevo cupón'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {edit && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}><TextField fullWidth size="small" label="Código" value={edit.codigo} onChange={(e) => setEdit({ ...edit, codigo: e.target.value.toUpperCase() })} disabled={!!edit.id} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Descripción" value={edit.descripcion || ''} onChange={set('descripcion')} /></Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" select label="Tipo" value={edit.tipo} onChange={set('tipo')}>
                  <MenuItem value="porcentaje">Porcentaje</MenuItem><MenuItem value="monto">Monto fijo (CLP)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField fullWidth size="small" type="number" label={edit.tipo === 'monto' ? 'Monto CLP' : '% de descuento'} value={edit.valor} onChange={set('valor')} /></Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" select label="Plan" value={edit.plan_codigo} onChange={set('plan_codigo')}>
                  <MenuItem value="">Todos los planes</MenuItem>
                  {planes.map((p) => <MenuItem key={p.codigo} value={p.codigo}>{p.nombre}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Usos máximos" value={edit.usos_max} onChange={set('usos_max')} helperText="Vacío = ilimitado" /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Vence" InputLabelProps={{ shrink: true }} value={edit.vence_at} onChange={set('vence_at')} /></Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch checked={!!edit.un_uso_por_usuario} onChange={(e) => setEdit({ ...edit, un_uso_por_usuario: e.target.checked })} />} label="1 uso por usuario" />
                <FormControlLabel control={<Switch checked={!!edit.activo} onChange={(e) => setEdit({ ...edit, activo: e.target.checked })} />} label="Activo" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEdit(null)}>Cancelar</Button><Button variant="contained" onClick={guardar}>Guardar</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCupones;
